import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyAPI, servicesAPI, testimonialsAPI } from '../utils/api';
import { MapPin, Briefcase, Plane, Clock, Navigation, Building, CheckCircle, Star, Users, Shield, Award } from 'lucide-react';
import Seo from '../components/Seo';

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'TaxiService',
  name: 'Ecoway Cabs',
  description: 'Corporate employee transportation, intercity travel, and airport transfer services across India since 2012.',
  url: 'https://ecowaycabs.com',
  logo: 'https://ecowaycabs.com/ecoway-logo.jpeg',
  image: 'https://ecowaycabs.com/ecoway-logo.jpeg',
  areaServed: 'IN',
  provider: {
    '@type': 'Organization',
    name: 'Ecoway Cabs',
    foundingDate: '2012',
  },
};

const iconMap = { MapPin, Briefcase, Plane, Clock, Navigation, Building };

const MarketingHome = () => {
  const navigate = useNavigate();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [companyRes, servicesRes, testimonialsRes] = await Promise.all([
        companyAPI.getInfo(),
        servicesAPI.getAll(),
        testimonialsAPI.getAll(),
      ]);
      setCompanyInfo(companyRes.data);
      setServices(servicesRes.data);
      setTestimonials(testimonialsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const hc = companyInfo?.home_content || {};

  return (
    <div className="min-h-screen">
      <Seo
        pageKey="home"
        seo={companyInfo?.seo}
        defaults={{
          title: 'Ecoway Cabs | Corporate & Intercity Cab Services in India Since 2012',
          description: 'Ecoway Cabs offers reliable corporate employee transportation, intercity travel, and airport transfers across India. Trusted by 50+ MNCs since 2012. Get a free quote today.',
          keywords: 'corporate cab services, employee transportation, intercity cabs, airport transfer, MNC transportation, business travel India, cab booking',
        }}
        path="/"
        jsonLd={homeJsonLd}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-24">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{companyInfo?.company_name || 'Ecoway Cabs'}</h1>
            <p className="text-2xl md:text-3xl text-blue-100 mb-4">{companyInfo?.tagline}</p>
            <p className="text-lg text-blue-200 mb-8 max-w-3xl mx-auto">Established since {companyInfo?.established_year || 2012} | {hc.hero_subtitle || 'Serving MNCs across India'}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/quote')}>
                Get a Quote
              </Button>
              <Button size="lg" className="border-2 border-white bg-transparent text-white hover:bg-white hover:text-blue-900 transition-all" onClick={() => navigate('/services')}>
                View Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-900 mb-2">{hc.stat1_value || '13+'}</div>
              <div className="text-gray-600">{hc.stat1_label || 'Years Experience'}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-900 mb-2">{hc.stat2_value || '50+'}</div>
              <div className="text-gray-600">{hc.stat2_label || 'Corporate Clients'}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-900 mb-2">{hc.stat3_value || '100+'}</div>
              <div className="text-gray-600">{hc.stat3_label || 'Cities Covered'}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-900 mb-2">{hc.stat4_value || '24/7'}</div>
              <div className="text-gray-600">{hc.stat4_label || 'Support'}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{hc.services_heading || 'Our Services'}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">{hc.services_subheading || 'Comprehensive transportation solutions for all your needs'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon] || MapPin;
              return (
                <Card key={service.id} className="hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/services')}>
                  <CardContent className="p-6">
                    <div className="bg-blue-100 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{hc.why_heading || 'Why Choose Ecoway Cabs?'}</h2>
            <p className="text-xl text-gray-600">{hc.why_subheading || 'The trusted choice for corporate transportation'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{hc.why_card1_title || 'Reliable & Safe'}</h3>
              <p className="text-gray-600">{hc.why_card1_desc || 'Verified drivers, well-maintained vehicles, and GPS tracking'}</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{hc.why_card2_title || 'Cost-Effective'}</h3>
              <p className="text-gray-600">{hc.why_card2_desc || 'Transparent pricing with no hidden charges'}</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-10 w-10 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{hc.why_card3_title || 'Professional Service'}</h3>
              <p className="text-gray-600">{hc.why_card3_desc || 'Courteous drivers, punctual service, dedicated support'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">{hc.testimonials_heading || 'What Our Clients Say'}</h2>
              <p className="text-xl text-gray-600">{hc.testimonials_subheading || 'Trusted by leading organizations'}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="bg-white">
                  <CardContent className="p-6">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-4 italic">"{testimonial.message}"</p>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.client_name}</div>
                      <div className="text-sm text-gray-600">{testimonial.designation}</div>
                      <div className="text-sm text-blue-600">{testimonial.company}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-indigo-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">{hc.cta_heading || 'Ready to Get Started?'}</h2>
          <p className="text-xl text-blue-100 mb-8">{hc.cta_subheading || 'Contact us today for a customized transportation solution'}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/quote')}>
              Request a Quote
            </Button>
            <Button size="lg" className="border-2 border-white bg-transparent text-white hover:bg-white hover:text-blue-900 transition-all" onClick={() => navigate('/contact')}>
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarketingHome;
